import { StringTMap } from '@@types/common';
import SideApp from '@@types/SideApp';
import SideAppContent, { SideAppContentV1 } from '@@types/SideAppContent';

import { TableNames } from '~/constants';
import { api } from '~/stores/providers/Api2';
import idb from '~/stores/providers/Idb';
import { eitherBy, keyBy } from '~/utils/fnUtil/fnUtil';
import { throttle } from 'lodash';
import { action, comparer, computed, observable, reaction, toJS } from 'mobx';
import {
  assoc,
  assocPath,
  curry,
  defaultTo,
  flatten,
  keys,
  mapObjIndexed,
  path,
  pick,
  pipe,
  pluck,
  prop,
  propOr,
  tap,
} from 'ramda';
import flip from 'ramda/es/flip';

type ReadItemMap = {
  __type__;
};

class SideAppsStore {
  constructor() {
    reaction(() => this.readItemIdMap, this.handleReadItemIdMap, { equals: comparer.shallow });
  }

  @observable
  readItemIdMap: StringTMap<ReadItemMap> = {};

  @observable
  currentSideAppId: string | null = null;

  @observable
  currentSideTabId = null;

  @observable
  sideAppContents: StringTMap<SideAppContent> = {};

  @observable
  sideAppMap: StringTMap<SideApp> = {};

  @observable
  sideAppOrder: string[] = [];

  @computed
  get version(): number {
    return eitherBy(prop('version'), path(['schema', 'version']))(this.currentSideAppContents);
  }

  @computed
  get currentSideApp() {
    return this.currentSideAppId && this.sideAppMap[this.currentSideAppId];
  }

  @computed
  get currentSideAppContents(): SideAppContent | null {
    if (!this.currentSideAppId) return null;
    return this.sideAppContents[this.currentSideAppId];
  }

  @computed
  get currentSideAppReadItemIdMap() {
    if (!this.currentSideAppId) return {};
    return this.readItemIdMap[this.currentSideAppId];
  }

  @action
  setSideAppOrder = (sideAppList) => {
    this.sideAppOrder = pluck<'id', SideApp>('id', sideAppList);
  };

  @action
  setReadItemIdMap = curry((sideAppId: string, readItemIdMap) => {
    this.readItemIdMap = assoc(sideAppId, readItemIdMap, this.readItemIdMap);
  });

  fetchSideApps = (tenantId: string) => {
    api.sideApp
      .fetchSideApps(tenantId)
      .then(tap(this.setSideAppOrder))
      .then(keyBy('id'))
      .then(this.setSideAppMap)
      .catch(() => {});
  };

  fetchSideAppContents = curry(async (tenantId: string, sideAppId: string) => {
    const response = await Promise.all([
      idb.get(TableNames.SideAppReadURL, sideAppId).then(defaultTo({})),
      api.sideApp.fetchSideAppContents(tenantId, sideAppId),
    ]);
    const [readItems, appContents] = response;
    this.setReadItemIdMap(sideAppId, readItems);
    this.setSideAppContents(sideAppId, appContents as SideAppContentV1);
    this.removeNoNeedReadInfoItemIdMap(sideAppId, appContents as SideAppContentV1, readItems);
    return response;
  });

  private removeNoNeedReadInfoItemIdMap = (sideAppId, appContents: SideAppContent, readItems) => {
    pipe<any, any, any, any, any, any>(
      this.getItemsByLayout,
      keyBy('id'),
      keys,
      flip(pick)(readItems),
      this.setReadItemIdMap(sideAppId)
    )(appContents);
  };

  private getItemsByLayout = (appContents: SideAppContentV1) => {
    if (appContents.layout === 'A') {
      return appContents.contents;
    } else if (appContents.layout === 'B') {
      return pipe<any, any, any, any>(propOr([], 'contents'), pluck('items'), flatten)(appContents);
    }
  };

  private handleReadItemIdMap = throttle((readItemIdMap) => {
    mapObjIndexed((readIdMap: ReadItemMap, sideAppId) => {
      idb.put(TableNames.SideAppReadURL, sideAppId, toJS(readIdMap));
    }, readItemIdMap);
  }, 200);

  @action
  setCurrentSideApp = (sideAppId: string | null) => {
    this.currentSideAppId = sideAppId;
  };

  @action
  setCurrentTabId = (tabId: string | null) => {
    this.currentSideTabId = tabId;
  };

  @action
  setSideAppContents = curry((sideAppId: string, contents: SideAppContent) => {
    this.sideAppContents[sideAppId] = contents;
  });

  @action
  setSideAppMap = (map) => {
    this.sideAppMap = map;
  };

  @action
  setReadItemId = (sideAppId: string, itemId: string) => {
    this.readItemIdMap = assocPath([sideAppId, itemId], true, this.readItemIdMap);
  };

  @action
  removeSideApp = (sideAppId: string) => {
    delete this.sideAppMap[sideAppId];
    delete this.sideAppContents[sideAppId];
  };
}

export default SideAppsStore;

export const sideAppsStore = new SideAppsStore();
